import React, { useEffect, useRef, useState } from 'react';
import { Star } from 'lucide-react';
import { useSiteConfig } from '../../lib/siteConfig';

export const Container = ({ className = '', children }) => (
  <div className={`mx-auto w-full max-w-[1160px] px-5 sm:px-8 ${className}`}>{children}</div>
);

export const Logo = ({ testId = 'brand-logo' }) => {
  const { brand_name } = useSiteConfig();
  return (
    <a href="/" data-testid={testId} className="flex items-center gap-2.5 rounded-xl">
      <img src="/bc-logo-gold.png" alt="" width="160" height="195" className="h-9 w-auto shrink-0 sm:h-10" />
      <span className="whitespace-nowrap font-display text-base font-bold tracking-tight text-white sm:text-lg">
        {brand_name}
      </span>
    </a>
  );
};

export const Eyebrow = ({ children }) => (
  <p className="font-display text-xs font-bold uppercase tracking-[0.16em] text-brand">{children}</p>
);

export const Stars = ({ onDark = false, size = 16 }) => (
  <span className="inline-flex gap-0.5" aria-label="5 von 5 Sternen" role="img">
    {[0, 1, 2, 3, 4].map((i) => (
      <Star key={i} size={size} className={onDark ? 'fill-starlight text-starlight' : 'fill-star text-star'} aria-hidden="true" />
    ))}
  </span>
);

const btnBase =
  'inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full px-7 py-3 text-[15px] font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';

export const btn = {
  primary: `${btnBase} gold-btn text-paper shadow-[0_8px_30px_-8px_rgba(232,203,122,0.55)] focus-visible:ring-brand focus-visible:ring-offset-paper`,
  outlineLight: `${btnBase} border border-line text-white hover:border-brand/60 hover:bg-white/5 focus-visible:ring-brand focus-visible:ring-offset-paper`,
  outlineDark: `${btnBase} border border-line bg-navy text-white hover:border-brand/60 focus-visible:ring-brand focus-visible:ring-offset-paper`,
  navy: `${btnBase} gold-btn text-paper focus-visible:ring-brand focus-visible:ring-offset-paper`,
  white: `${btnBase} gold-btn text-paper focus-visible:ring-brand focus-visible:ring-offset-paper`,
};

export const Reveal = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || !('IntersectionObserver' in window)) { setShown(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={`reveal ${shown ? 'reveal-in' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

export const H2 = ({ children, className = '' }) => (
  <h2 className={`mt-3 font-serif text-[30px] font-bold leading-[1.15] text-white sm:text-4xl lg:text-[44px] ${className}`}>
    {children}
  </h2>
);
